import React, { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { ArrowRight, Sparkles } from 'lucide-react';

const StatItem = ({ stat, index }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [displayValue, setDisplayValue] = useState("0");
  const statRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    if (statRef.current) observer.observe(statRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const value = stat.value;
    const hasPlus = value.includes('+');
    const hasPercent = value.includes('%');
    const hasSlash = value.includes('/');

    const numericPart = parseInt(value);
    const isNumber = !isNaN(numericPart);

    if (isNumber) {
      const target = numericPart;
      const duration = 2000;
      const steps = 60;
      const increment = target / steps;
      let current = 0;

      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          current = target;
          clearInterval(timer);
        }

        let newDisplay = Math.floor(current).toString();
        if (hasPlus) newDisplay += '+';
        if (hasPercent) newDisplay += '%';
        if (hasSlash) newDisplay += '/7';

        setDisplayValue(newDisplay);
      }, duration / steps);

      return () => clearInterval(timer);
    } else {
      setDisplayValue(value);
    }
  }, [isVisible, stat.value]);

  return (
    <div
      ref={statRef}
      className={`text-center group transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      <div className="text-3xl md:text-4xl font-bold text-[#2d4f4f] mb-2 transform group-hover:scale-110 transition-transform duration-300">
        {displayValue}
      </div>
      <div className="text-sm md:text-base text-gray-600">{stat.label}</div>
    </div>
  );
};

const HeroSection = ({ data, stats }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToWork = () => {
    const element = document.getElementById('work');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-50 pt-20 overflow-hidden">

      {/* 1. UPGRADED BACKGROUND: Added the Architect Grid pattern behind the glowing orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        <div className="absolute top-20 right-10 w-72 h-72 bg-[#2d4f4f]/5 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#d4a574]/5 rounded-full blur-3xl animate-float-delay"></div>
        <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-[#2d4f4f]/3 rounded-full blur-3xl animate-pulse-slow"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
        <div className="text-center max-w-4xl mx-auto">

          {/* Badge */}
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 bg-[#2d4f4f]/10 rounded-full mb-8 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
          >
            <Sparkles className="w-4 h-4 text-[#2d4f4f]" />
            <span className="text-sm font-medium text-[#2d4f4f]">Web Development Excellence</span>
          </div>

          {/* 2. TYPOGRAPHY: Added tracking-tight for that expensive editorial kerning */}
          <h1
            className={`text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6 transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
          >
            <span className="inline-block animate-fade-in-up text-gray-900">{data.title}</span>
            <br />
            <span className="inline-block animate-fade-in-up animation-delay-200 bg-gradient-to-r from-[#2d4f4f] via-[#1a3a3a] to-[#2d4f4f] bg-clip-text text-transparent bg-[length:200%_100%] animate-gradient">
              {data.highlight}
            </span>
          </h1>

          {/* Description */}
          <p
            className={`text-xl md:text-2xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
          >
            {data.description}
          </p>

          {/* CTA Buttons - Reduced bottom margin to make room for the trust badge */}
          <div
            className={`flex flex-col sm:flex-row items-center justify-center gap-4 mb-10 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
          >
            {/* 3. PREMIUM BUTTON: Added glowing shadow and lift effect */}
            <Button
              onClick={scrollToWork}
              size="lg"
              className="bg-[#2d4f4f] hover:bg-[#1a3a3a] text-white px-8 py-6 text-lg group transition-all duration-300 shadow-[0_0_20px_rgba(45,79,79,0.3)] hover:shadow-[0_0_30px_rgba(45,79,79,0.5)] hover:-translate-y-1"
            >
              {data.cta}
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </Button>
            <Button
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              size="lg"
              variant="outline"
              className="border-2 border-[#2d4f4f] text-[#2d4f4f] hover:bg-[#2d4f4f] hover:text-white px-8 py-6 text-lg transition-all duration-300 bg-white/50 backdrop-blur-sm"
            >
              Let's Talk
            </Button>
          </div>

          {/* 4. MICRO-TRUST BADGE: Social proof right below the buttons */}
          <div
            className={`flex items-center justify-center gap-4 mb-20 transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
          >
            <div className="flex -space-x-3">
              {/* Note: Using Pravatar for instant mockup faces. You can replace these with client logos later! */}
              {[12, 32, 44, 57].map((imgId, i) => (
                <div key={i} className="w-10 h-10 rounded-full border-2 border-white bg-gray-100 flex items-center justify-center overflow-hidden shadow-sm">
                  <img src={`https://i.pravatar.cc/100?img=${imgId}`} alt="Client Avatar" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
            <div className="text-sm text-gray-600 text-left">
              <div className="flex items-center gap-1 text-[#d4a574] mb-0.5">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="font-medium text-gray-800">Trusted by 30+ growing businesses</p>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-3xl mx-auto">
            {stats.map((stat, index) => (
              <StatItem key={index} stat={stat} index={index} />
            ))}
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 w-full h-48 bg-gradient-to-b from-transparent to-zinc-950 pointer-events-none"></div>

      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-zinc-600 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-zinc-500 rounded-full"></div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;